<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('CMS-Dateimanager') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/cms">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                    <button
                        :aria-label="$t('Filter menu')"
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        @click="toggleFilterSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            class="stage-filter-active-dot"
                            aria-hidden="true"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="fetchAll"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <input ref="fileInput" hidden type="file" multiple @change="uploadFile($event)">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <aside
                    :aria-hidden="!filterSidebarOpen"
                    class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-40"
                    :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                >
                    <div class="stage-filter-body">
                        <div class="stage-filter-title-row">
                            <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                            <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                        </div>

                        <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                        <div class="stage-filter-row mb-5">
                            <button class="stage-filter-sort-btn text-sm leading-5 font-medium" type="button" @click="toggleSort">
                                <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                            </button>
                            <div class="stage-filter-sort-select-wrap">
                                <select v-model="sort.column" class="stage-filter-sort-select block form-select text-sm leading-5 font-medium" @change="fetchFiles">
                                    <option value="updated_at">{{ $t('Letztes Update') }}</option>
                                    <option value="created_at">{{ $t('Upload') }}</option>
                                    <option value="name">{{ $t('Dateiname') }}</option>
                                    <option value="size">{{ $t('Dateigröße') }}</option>
                                    <option value="extension">{{ $t('Dateityp') }}</option>
                                </select>
                                <span class="stage-filter-select-icon">
                                    <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                </span>
                            </div>
                        </div>

                        <div class="stage-filter-label">{{ $t('Search') }}</div>
                        <div class="mb-4">
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                </div>
                                <input
                                    v-model.lazy="filters.search"
                                    :placeholder="$t('Search')"
                                    class="stage-filter-input form-input block pl-10 text-sm"
                                    @change="fetchFiles"
                                >
                            </div>
                        </div>

                        <div v-if="categories.length > 0" class="stage-filter-label">{{ $t('Kategorien') }}</div>
                        <div v-if="categories.length > 0" class="mb-3 border border-bordergray-100 rounded bg-white px-3 py-2 max-h-52 overflow-auto">
                            <label
                                v-for="category in categories"
                                :key="'sidebar-category-' + category.id"
                                class="flex items-center py-1 cursor-pointer"
                            >
                                <input
                                    :checked="isCategorySelected(category.id)"
                                    class="form-checkbox h-4 w-4 text-gray-600 mr-2"
                                    type="checkbox"
                                    @change="toggleCategoryFilter(category.id)"
                                >
                                <span class="text-sm text-gray-700 truncate">{{ category.name }}</span>
                                <span class="h-2.5 w-2.5 rounded-full ml-2 mr-2 flex-shrink-0" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                <span class="ml-auto text-sm text-gray-500">{{ category.files_count || 0 }}</span>
                            </label>
                        </div>

                        <div class="stage-filter-label">{{ $t('Items per page') }}</div>
                        <div class="mb-3">
                            <input-select
                                id="cms-file-manager-per-page"
                                v-model="perPage"
                                class="stage-filter-select"
                                :options="perPageOptions"
                                option-label="name"
                                @change="onPerPageChange"
                            />
                        </div>

                        <div v-if="hasActiveFilters" class="col-span-3">
                            <button
                                id="cms-file-manager-filter-reset-btn"
                                type="button"
                                class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                @click="resetFilters"
                            >
                                {{ $t('Reset selection') }}
                            </button>
                        </div>
                    </div>
                </aside>

                <div class="border-b border-ultralight">
                    <div class="stage-header-surface">
                        <div class="flex items-stretch justify-between min-h-[52px]">
                            <div class="inline-flex items-center px-4 border-r border-ultralight">
                                <button
                                    :class="viewMode === 'list' ? 'file-manager-view-btn-active' : ''"
                                    class="file-manager-view-btn file-manager-header-action-btn"
                                    type="button"
                                    @click="viewMode = 'list'"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.bars"></svg-vue>
                                </button>
                                <button
                                    :class="viewMode === 'grid' ? 'file-manager-view-btn-active' : ''"
                                    class="file-manager-view-btn file-manager-header-action-btn ml-2"
                                    type="button"
                                    @click="viewMode = 'grid'"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.grid-2"></svg-vue>
                                </button>
                            </div>

                            <div class="ml-auto inline-flex items-stretch">
                                <div v-on-clickaway="closeCategoryManager" class="relative inline-flex items-stretch border-l border-r border-ultralight">
                                    <button
                                        type="button"
                                        class="inline-flex items-center justify-center w-12 h-full text-gray-700 focus:outline-none file-manager-header-action-btn"
                                        :aria-label="$t('Kategorien')"
                                        @click.stop="toggleCategoryManager"
                                    >
                                        <svg-vue class="h-4 w-4 file-manager-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                    </button>
                                    <div v-show="categoryManagerOpen" class="origin-top-right z-40 absolute right-0 top-full mt-2 w-64 rounded-sm border border-bordergray-100 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.08)] overflow-hidden">
                                        <div class="overflow-hidden">
                                            <div class="border-b border-bordergray-100">
                                                <category-create-input
                                                    ref="categoryManagerInput"
                                                    v-model:color="categoryDraft.color"
                                                    v-model:name="categoryDraft.name"
                                                    :maxlength="120"
                                                    :placeholder="$t('Color and category').toString()"
                                                    @submit="createCategory"
                                                />
                                            </div>
                                            <div class="max-h-56 overflow-auto">
                                                <div
                                                    v-for="category in categories"
                                                    :key="'manager-category-' + category.id"
                                                    class="file-manager-category-row border-b border-bordergray-100 last:border-b-0 flex items-center"
                                                >
                                                    <div class="file-manager-category-row-content inline-flex items-center min-w-0 flex-1">
                                                        <span class="file-manager-category-row-dot rounded-full flex-shrink-0" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                        <span class="text-sm text-gray-700 truncate">{{ category.name }}</span>
                                                        <span class="text-xs text-gray-500 ml-2">({{ category.files_count || 0 }})</span>
                                                    </div>
                                                    <div class="file-manager-category-row-actions">
                                                        <button
                                                            type="button"
                                                            class="file-manager-category-delete-btn text-gray-500 hover:text-gray-700 focus:outline-none inline-flex items-center justify-center"
                                                            aria-label="Delete category"
                                                            @click.stop="deleteCategory(category)"
                                                        >
                                                            <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="w-32 border-l border-ultralight">
                                    <button class="btn-stage-primary w-full h-full border-0 file-manager-header-action-btn" style="border-radius: 0;" type="button" @click="selectUploadFile">
                                        {{ $t('Hochladen') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <template v-if="!loading && files.length < 1">
                            <empty-state :title="$t('Keine CMS-Dateien gefunden')"/>
                        </template>

                        <template v-else-if="viewMode === 'list'">
                            <div class="hidden sm:block h-full">
                                <div class="h-full overflow-auto">
                                    <table class="table-listview table-listview--zebra file-manager-list-table">
                                        <thead>
                                        <tr>
                                            <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-title">{{ $t('Titel') }}</th>
                                            <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-type">{{ $t('Typ') }}</th>
                                            <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-size">{{ $t('Größe') }}</th>
                                            <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-upload">{{ $t('Upload von') }}</th>
                                            <th class="table-listview-l-th file-manager-list-sticky-th file-manager-col-update">{{ $t('Update') }}</th>
                                            <th class="table-listview-l-th table-listview-l-th-right file-manager-list-sticky-th file-manager-col-actions">{{ $t('Aktionen') }}</th>
                                        </tr>
                                        </thead>
                                        <tbody class="bg-white">
                                        <tr v-for="item in files" :key="rowKey(item)">
                                            <td class="table-listview-l-td whitespace-no-wrap file-manager-cell-nowrap file-manager-col-title">
                                                <div class="flex items-center text-sm font-medium text-gray-900 min-w-0 w-full">
                                                    <svg-vue class="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" :icon="fileIcon(item.extension)"></svg-vue>
                                                    <span class="inline-flex items-center min-w-0 max-w-full">
                                                        <span class="file-manager-title-text">{{ item.name }}</span>
                                                        <span v-if="item.category" class="file-manager-list-category-dot ml-2 flex-shrink-0" :style="{ backgroundColor: getCategoryColor(item) }"></span>
                                                        <app-tooltip v-if="item.short_description" :text="shortDescriptionTooltip(item.short_description)" width-class="max-w-xs break-words" class="ml-2 flex-shrink-0 inline-flex">
                                                            <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.regular.circle-info"></svg-vue>
                                                        </app-tooltip>
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 uppercase file-manager-cell-nowrap file-manager-col-type">{{ item.extension || '-' }}</td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 file-manager-cell-nowrap file-manager-col-size">{{ item.size_readable }}</td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 file-manager-cell-nowrap file-manager-col-upload">
                                                <div class="flex items-center min-w-0 w-full">
                                                    <img
                                                        :src="getUserAvatar(item.created_by_user)"
                                                        :alt="item.created_by_user ? item.created_by_user.name : 'Avatar'"
                                                        :title="item.created_by_user ? item.created_by_user.name : ''"
                                                        class="h-6 w-6 rounded-full mr-3"
                                                    >
                                                    <span class="truncate block">{{ item.created_by_user ? item.created_by_user.name : '-' }}</span>
                                                </div>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-500 file-manager-cell-nowrap file-manager-col-update">
                                                <span class="truncate block">{{ item.updated_at ? $momentFormatDateTimeAgo(item.updated_at) : '-' }}</span>
                                            </td>
                                            <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700 text-right file-manager-cell-nowrap file-manager-col-actions">
                                                <div class="inline-flex items-center justify-end">
                                                    <button
                                                        v-if="canPreviewImage(item)"
                                                        type="button"
                                                        class="inline-flex"
                                                        @click.stop="openImagePreview(item)"
                                                    >
                                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.eye"></svg-vue>
                                                    </button>
                                                    <button type="button" class="inline-flex ml-3" @click.stop="copyFileUrl(item)">
                                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.clipboard"></svg-vue>
                                                    </button>
                                                    <a :href="item.url" class="inline-flex ml-3" target="_blank" rel="noopener">
                                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                                                    </a>
                                                    <div v-if="categories.length > 0" v-on-clickaway="closeItemCategoryMenu" class="relative inline-flex ml-3">
                                                        <button
                                                            type="button"
                                                            class="inline-flex items-center justify-center text-gray-700"
                                                            @click.stop="toggleItemCategoryMenu(item)"
                                                        >
                                                            <svg-vue class="h-4 w-4 file-manager-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                                        </button>
                                                        <div v-show="isItemCategoryMenuOpen(item)" class="origin-top-right z-40 absolute right-0 mt-2 w-52 rounded shadow-lg">
                                                            <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100 max-h-56 overflow-auto" role="menu">
                                                                <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" type="button" @click.stop="assignCategory(item, null)">
                                                                    Keine Kategorie
                                                                </button>
                                                                <button
                                                                    v-for="category in categories"
                                                                    :key="'assign-category-list-' + item.id + '-' + category.id"
                                                                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                                    type="button"
                                                                    @click.stop="assignCategory(item, category.id)"
                                                                >
                                                                    <span class="inline-flex items-center">
                                                                        <span class="h-2.5 w-2.5 rounded-full mr-2" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                                        <span>{{ category.name }}</span>
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-on-clickaway="closeActionMenu" class="relative inline-flex ml-3">
                                                        <button type="button" class="inline-flex" @click.stop="toggleActionMenu(item)">
                                                            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                        </button>
                                                        <div v-show="isActionMenuOpen(item)" class="origin-top-right z-40 absolute right-0 mt-2 w-44 rounded shadow-lg">
                                                            <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100" role="menu">
                                                                <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" type="button" @click.stop="openEditFromMenu(item)">
                                                                    Editieren
                                                                </button>
                                                                <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" type="button" @click.stop="openDeleteModal(item)">
                                                                    Löschen
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <div class="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 file-manager-tile-grid">
                                <div
                                    v-for="item in files"
                                    :key="'grid-' + rowKey(item)"
                                    class="bg-white border border-bordergray-100 rounded file-manager-tile-item"
                                    :style="{ borderLeftColor: item.category ? getCategoryColor(item) : undefined, borderLeftWidth: item.category ? 'calc(0.28rem + 1px)' : undefined }"
                                >
                                    <div
                                        class="px-6 py-6 text-center file-manager-tile-header relative cursor-pointer"
                                        role="button"
                                        tabindex="0"
                                        @click="openEdit(item)"
                                        @keydown.enter.prevent="openEdit(item)"
                                        @keydown.space.prevent="openEdit(item)"
                                    >
                                        <div v-if="categories.length > 0" v-on-clickaway="closeItemCategoryMenu" class="absolute left-3 top-3 inline-flex">
                                            <button type="button" class="inline-flex items-center justify-center text-gray-700" @click.stop="toggleItemCategoryMenu(item)">
                                                <svg-vue class="h-4 w-4 file-manager-category-tag-icon" icon="font-awesome.regular.label"></svg-vue>
                                            </button>
                                            <div v-show="isItemCategoryMenuOpen(item)" class="origin-top-left z-40 absolute left-0 top-full mt-2 w-52 rounded shadow-lg">
                                                <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100 max-h-56 overflow-auto" role="menu">
                                                    <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" type="button" @click.stop="assignCategory(item, null)">
                                                        Keine Kategorie
                                                    </button>
                                                    <button
                                                        v-for="category in categories"
                                                        :key="'assign-category-grid-top-' + item.id + '-' + category.id"
                                                        class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                        type="button"
                                                        @click.stop="assignCategory(item, category.id)"
                                                    >
                                                        <span class="inline-flex items-center">
                                                            <span class="h-2.5 w-2.5 rounded-full mr-2" :style="{ backgroundColor: category.color || 'var(--ui-text-subtle)' }"></span>
                                                            <span>{{ category.name }}</span>
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="h-24 w-24 mx-auto mb-4 rounded border border-bordergray-100 bg-gray-50 overflow-hidden flex items-center justify-center">
                                            <img v-if="canRenderImage(item)" :src="item.url" :alt="item.name" class="h-full w-full object-cover">
                                            <svg-vue v-else class="h-14 w-14" :icon="fileIcon(item.extension)"></svg-vue>
                                        </div>
                                        <div class="flex items-center justify-center w-full text-sm text-gray-800 px-2 min-w-0">
                                            <span class="inline-flex items-center min-w-0 max-w-full">
                                                <span class="file-manager-title-text">{{ item.name }}</span>
                                                <app-tooltip v-if="item.short_description" :text="shortDescriptionTooltip(item.short_description)" width-class="max-w-xs break-words" class="ml-2 flex-shrink-0 inline-flex">
                                                    <svg-vue class="h-4 w-4 text-gray-500 flex-shrink-0" icon="font-awesome.regular.circle-info"></svg-vue>
                                                </app-tooltip>
                                            </span>
                                        </div>
                                        <div class="mt-1 text-xs text-gray-500 text-center">
                                            {{ (item.extension || '-').toUpperCase() }}, {{ item.size_readable }}
                                        </div>
                                    </div>
                                    <div class="px-4 border-t border-bordergray-100 flex items-center justify-between h-12 text-xs text-gray-500">
                                        <div class="inline-flex items-center">
                                            <img
                                                :src="getUserAvatar(item.created_by_user)"
                                                :alt="item.created_by_user ? item.created_by_user.name : 'Avatar'"
                                                :title="item.created_by_user ? item.created_by_user.name : ''"
                                                class="h-6 w-6 rounded-full"
                                            >
                                        </div>
                                        <div class="inline-flex items-center">
                                            <button v-if="canPreviewImage(item)" type="button" class="inline-flex" @click.stop="openImagePreview(item)">
                                                <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.eye"></svg-vue>
                                            </button>
                                            <button type="button" class="inline-flex ml-3" @click.stop="copyFileUrl(item)">
                                                <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.clipboard"></svg-vue>
                                            </button>
                                            <a :href="item.url" class="inline-flex ml-3" target="_blank" rel="noopener">
                                                <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                                            </a>
                                            <div v-on-clickaway="closeActionMenu" class="relative inline-flex ml-3">
                                                <button type="button" class="inline-flex" @click.stop="toggleActionMenu(item)">
                                                    <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                                </button>
                                                <div v-show="isActionMenuOpen(item)" class="origin-top-right z-40 absolute right-0 bottom-full mb-2 w-44 rounded shadow-lg">
                                                    <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100" role="menu">
                                                        <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" type="button" @click.stop="openEditFromMenu(item)">
                                                            Editieren
                                                        </button>
                                                        <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" type="button" @click.stop="openDeleteModal(item)">
                                                            Löschen
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    nav-class="relative z-30"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>

        <confirm-dialog :open="deleteModal" title="Datei löschen" text="Soll diese CMS-Datei wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Löschen" confirm-variant="danger" @close="closeDeleteModal" @confirm="deleteFile" />

        <div v-show="previewModal.open" class="app-dialog-root fixed z-50 inset-0 overflow-y-auto" @click.self="closeImagePreview">
            <div class="flex items-center justify-center min-h-screen px-4 py-8">
                <div class="app-dialog-overlay fixed inset-0 z-40 transition-opacity"><div class="absolute inset-0 bg-black opacity-80"></div></div>
                <div class="relative z-50 max-w-6xl w-full flex items-center justify-center">
                    <div class="relative inline-block max-w-full">
                        <button
                            type="button"
                            class="absolute -top-9 right-0 z-10 inline-flex items-center justify-center w-8 h-8 p-0 bg-transparent border-0 text-white leading-none focus:outline-none"
                            @click="closeImagePreview"
                        >
                            <svg viewBox="0 0 24 24" fill="none" class="h-6 w-6">
                                <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <img :src="previewModal.url" :alt="previewModal.title || $t('Dateivorschau')" class="block max-h-[85vh] w-auto max-w-full object-contain">
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import CategoryCreateInput from "@/components/forms/category-create-input.vue";
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {UI_COLORS} from '@/utils/ui-colors';
import {
    queryNumber,
    queryNumberArray,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";
import {
    buildCmsShortDescriptionTooltip,
    getCmsFileCategoryColor,
    getCmsFileRowKey,
    isCmsImageFileExtension,
    resolveCmsFileIcon,
    resolveCmsFileUserAvatar,
} from "@/utils/cms-file-manager";

export default {
    name: "cms-file-manager-list",
    components: {
        CategoryCreateInput,
        ConfirmDialog,
    },
    mixins: [clickaway],
    metaInfo() {
        return {
            title: this.$t('CMS-Dateimanager')
        }
    },
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const allowedSortColumns = ['updated_at', 'created_at', 'name', 'size', 'extension'];
        const initialSortColumn = queryString(routeQuery, 'list_sort_column', 'name');
        const initialSortOrder = queryString(routeQuery, 'list_sort_order', 'asc') === 'desc' ? 'desc' : 'asc';

        return {
            loading: false,
            filterSidebarOpen: false,
            viewMode: 'grid',
            files: [],
            filters: {
                search: queryString(routeQuery, 'list_search', null),
            },
            sort: {
                order: initialSortOrder,
                column: allowedSortColumns.includes(initialSortColumn) ? initialSortColumn : 'name',
            },
            page: queryNumber(routeQuery, 'list_page', 1),
            perPage: queryNumber(routeQuery, 'list_per_page', 25),
            pagination: {
                currentPage: 1,
                perPage: 25,
                total: 0,
                totalPages: 1,
            },
            categories: [],
            selectedCategoryIds: queryNumberArray(routeQuery, 'list_categories'),
            openActionMenu: null,
            openCategoryMenu: null,
            categoryManagerOpen: false,
            deleteModal: false,
            pendingDeleteItem: null,
            previewModal: {
                open: false,
                url: '',
                title: '',
            },
            categoryDraft: {
                name: '',
                color: UI_COLORS.text.subtle,
            },
        };
    },
    mounted() {
        this.fetchAll();
    },
    computed: {
        perPageOptions() {
            const defaults = [10, 25, 50, 100];
            const current = Number(this.perPage || 0);
            const values = defaults.includes(current) ? defaults : [...defaults, current].sort((a, b) => a - b);
            return values.map((value) => ({id: value, name: value}));
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || this.sort.order !== 'asc'
                || this.sort.column !== 'name'
                || this.selectedCategoryIds.length > 0
                || Number(this.perPage || 0) !== 25;
        },
    },
    methods: {
        fetchAll() {
            this.fetchOptions();
            this.fetchFiles();
        },
        fetchOptions() {
            axios.get('api/workspace/admin/cms/file-manager/options').then((response) => {
                this.categories = response.data.categories || [];
            });
        },
        fetchFiles() {
            this.syncCmsFileManagerListRouteState();
            this.loading = true;
            this.openActionMenu = null;
            this.openCategoryMenu = null;

            axios.get('api/workspace/admin/cms/file-manager', {
                params: {
                    page: this.page,
                    perPage: this.perPage,
                    search: this.filters.search,
                    sort: this.sort,
                    category_ids: this.selectedCategoryIds,
                }
            }).then((response) => {
                this.files = response.data.items || [];
                this.pagination = response.data.pagination || this.pagination;
                if (Array.isArray(response.data.categories)) {
                    this.categories = response.data.categories;
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        syncCmsFileManagerListRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_categories',
            ];

            let nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_page', Number(this.page || 1) === 1 ? null : Number(this.page || 1));
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 0) === 25 ? null : Number(this.perPage || 0));
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column === 'name' ? null : this.sort.column);
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order === 'asc' ? null : this.sort.order);
            setQueryValue(nextQuery, 'list_search', (this.filters.search || '').trim() || null);
            setQueryValue(nextQuery, 'list_categories', this.selectedCategoryIds.length > 0 ? this.selectedCategoryIds : null);

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        },
        selectUploadFile() {
            this.$refs.fileInput.click();
        },
        uploadFile(event) {
            if (!event.target.files || !event.target.files.length) {
                return;
            }

            const selectedFiles = Array.from(event.target.files);
            this.loading = true;
            const uploads = selectedFiles.map((file) => {
                const formData = new FormData();
                formData.append('file', file);
                return axios.post('api/workspace/admin/cms/file-manager', formData, {headers: {'Content-Type': 'multipart/form-data'}});
            });

            Promise.allSettled(uploads).then((results) => {
                const successCount = results.filter((result) => result.status === 'fulfilled').length;
                const failedCount = results.length - successCount;
                const firstFailureMessage = results
                    .filter((result) => result.status === 'rejected')
                    .map((result) => result.reason?.response?.data?.message || null)
                    .find((message) => !!message);

                this.loading = false;
                this.$refs.fileInput.value = null;
                this.fetchAll();

                if (successCount > 0 && failedCount === 0) {
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: successCount === 1 ? '1 Datei erfolgreich hochgeladen.' : `${successCount} Dateien erfolgreich hochgeladen.`,
                        type: 'success'
                    });
                    return;
                }

                if (successCount > 0) {
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: firstFailureMessage
                            ? `${successCount} Dateien hochgeladen, ${failedCount} fehlgeschlagen. ${firstFailureMessage}`
                            : `${successCount} Dateien hochgeladen, ${failedCount} fehlgeschlagen.`,
                        type: 'warn'
                    });
                    return;
                }

                this.$notify({
                    title: firstFailureMessage ? this.$i18n.t('Warning').toString() : this.$i18n.t('Error').toString(),
                    text: firstFailureMessage || 'Keine Datei konnte hochgeladen werden.',
                    type: firstFailureMessage ? 'warn' : 'error'
                });
            }).catch(() => {
                this.loading = false;
                this.$refs.fileInput.value = null;
            });
        },
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        onPerPageChange() {
            this.page = 1;
            this.fetchFiles();
        },
        toggleSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.fetchFiles();
        },
        resetFilters() {
            this.filters.search = null;
            this.sort.order = 'asc';
            this.sort.column = 'name';
            this.selectedCategoryIds = [];
            this.perPage = 25;
            this.page = 1;
            this.fetchAll();
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.fetchFiles();
            }
        },
        isCategorySelected(categoryId) {
            return this.selectedCategoryIds.includes(Number(categoryId));
        },
        toggleCategoryFilter(categoryId) {
            const id = Number(categoryId);
            if (id < 1) {
                return;
            }

            if (this.selectedCategoryIds.includes(id)) {
                this.selectedCategoryIds = this.selectedCategoryIds.filter((selectedId) => selectedId !== id);
            } else {
                this.selectedCategoryIds = [...this.selectedCategoryIds, id];
            }

            this.page = 1;
            this.fetchFiles();
        },
        toggleCategoryManager() {
            const shouldOpen = !this.categoryManagerOpen;
            this.categoryManagerOpen = shouldOpen;
            this.closeActionMenu();
            this.closeItemCategoryMenu();

            if (shouldOpen) {
                this.$nextTick(() => {
                    const input = this.$refs.categoryManagerInput;
                    if (input && typeof input.focusInput === 'function') {
                        input.focusInput();
                    } else if (input && typeof input.focus === 'function') {
                        input.focus();
                    }
                });
            }
        },
        closeCategoryManager() {
            this.categoryManagerOpen = false;
        },
        createCategory() {
            const name = String(this.categoryDraft.name || '').trim();
            if (!name) {
                this.$notify({
                    title: this.$t('Error').toString(),
                    text: this.$t('Choose a color and a category text').toString(),
                    type: 'error',
                });
                return;
            }

            axios.post('api/workspace/admin/cms/file-manager/categories', {
                name,
                color: this.categoryDraft.color || UI_COLORS.text.subtle,
            }).then((response) => {
                this.categories = response.data.categories || this.categories;
                this.categoryDraft.name = '';
                this.categoryDraft.color = UI_COLORS.text.subtle;
                this.fetchFiles();
            });
        },
        deleteCategory(category) {
            if (!category || !category.id) {
                return;
            }

            axios.delete(`api/workspace/admin/cms/file-manager/categories/${category.id}`)
                .then((response) => {
                    const deletedCategoryId = Number(category.id);
                    this.categories = response.data.categories || [];
                    this.selectedCategoryIds = this.selectedCategoryIds.filter((id) => id !== deletedCategoryId);
                    this.fetchFiles();
                });
        },
        openEdit(item) {
            if (!item || !item.uuid) {
                return;
            }

            this.$router.push(routeWithReturnTo('/workspace/cms/file-manager/' + item.uuid + '/edit', this.$route, '/workspace/cms/file-manager'));
        },
        openEditFromMenu(item) {
            this.closeActionMenu();
            this.openEdit(item);
        },
        actionMenuKey(item) {
            return `action-${this.rowKey(item)}`;
        },
        categoryMenuKey(item) {
            return `category-${this.rowKey(item)}`;
        },
        isActionMenuOpen(item) {
            return this.openActionMenu === this.actionMenuKey(item);
        },
        isItemCategoryMenuOpen(item) {
            return this.openCategoryMenu === this.categoryMenuKey(item);
        },
        toggleActionMenu(item) {
            const key = this.actionMenuKey(item);
            this.openCategoryMenu = null;
            this.categoryManagerOpen = false;
            this.openActionMenu = this.openActionMenu === key ? null : key;
        },
        toggleItemCategoryMenu(item) {
            const key = this.categoryMenuKey(item);
            this.openActionMenu = null;
            this.categoryManagerOpen = false;
            this.openCategoryMenu = this.openCategoryMenu === key ? null : key;
        },
        closeActionMenu() {
            this.openActionMenu = null;
        },
        closeItemCategoryMenu() {
            this.openCategoryMenu = null;
        },
        assignCategory(item, categoryId) {
            if (!item || !item.uuid) {
                return;
            }

            axios.patch(`api/workspace/admin/cms/file-manager/${item.uuid}/category`, {
                category_id: categoryId || null,
            }).then(() => {
                const selectedCategory = categoryId
                    ? this.categories.find((category) => Number(category.id) === Number(categoryId))
                    : null;

                item.category_id = categoryId ? Number(categoryId) : null;
                item.category = selectedCategory ? {
                    id: Number(selectedCategory.id),
                    name: selectedCategory.name,
                    color: selectedCategory.color,
                } : null;
                this.closeItemCategoryMenu();
                this.fetchOptions();
            });
        },
        openDeleteModal(item) {
            this.pendingDeleteItem = item;
            this.closeActionMenu();
            this.closeItemCategoryMenu();
            this.closeCategoryManager();
            this.deleteModal = true;
        },
        closeDeleteModal() {
            this.deleteModal = false;
            this.pendingDeleteItem = null;
        },
        deleteFile() {
            const item = this.pendingDeleteItem;
            if (!item || !item.uuid) {
                this.closeDeleteModal();
                return;
            }

            this.loading = true;
            axios.delete('api/workspace/admin/cms/file-manager/' + item.uuid).then(() => {
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: 'Die Datei wurde gelöscht.',
                    type: 'success'
                });
                this.closeDeleteModal();
                this.fetchAll();
            }).catch(() => {
                this.loading = false;
                this.closeDeleteModal();
            });
        },
        async copyFileUrl(item) {
            const text = String(item?.url || '').trim();
            if (text === '') {
                return;
            }

            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const input = document.createElement('input');
                    input.value = text;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                }

                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: 'Öffentliche URL kopiert.',
                    type: 'success'
                });
            } catch (error) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: 'Die URL konnte nicht in die Zwischenablage kopiert werden.',
                    type: 'error'
                });
            }
        },
        openImagePreview(item) {
            if (!this.canPreviewImage(item)) {
                return;
            }

            this.previewModal = {
                open: true,
                url: item.url,
                title: item.name || 'Dateivorschau',
            };
        },
        closeImagePreview() {
            this.previewModal = {
                open: false,
                url: '',
                title: '',
            };
        },
        getCategoryColor: getCmsFileCategoryColor,
        rowKey: getCmsFileRowKey,
        fileIcon: resolveCmsFileIcon,
        getUserAvatar: resolveCmsFileUserAvatar,
        isImageFile: isCmsImageFileExtension,
        canRenderImage(item) {
            return !!item && this.isImageFile(item.extension) && item.file_exists !== false && !!item.url;
        },
        canPreviewImage(item) {
            return this.canRenderImage(item);
        },
        shortDescriptionTooltip: buildCmsShortDescriptionTooltip,
    }
}
</script>

<style scoped>
.file-manager-tile-item {
    position: relative;
    z-index: 1;
    overflow: visible;
}

.file-manager-list-category-dot {
    display: inline-flex;
    width: 0.625rem;
    min-width: 0.625rem;
    max-width: 0.625rem;
    height: 0.625rem;
    min-height: 0.625rem;
    max-height: 0.625rem;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    vertical-align: middle;
}

.file-manager-category-tag-icon {
    color: currentColor;
}

.file-manager-category-row {
    padding: 0.5rem 0;
}

.file-manager-category-row-content {
    padding-left: 0.7rem;
}

.file-manager-category-row-dot {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
}

.file-manager-category-row-actions {
    width: 2.35rem;
    min-width: 2.35rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.file-manager-category-delete-btn {
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease-in-out;
}

.file-manager-category-row:hover .file-manager-category-delete-btn {
    opacity: 1;
    pointer-events: auto;
}

.file-manager-tile-header {
    height: 13.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.file-manager-view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 1.75rem;
    width: 1.75rem;
    color: var(--ui-text-default);
    border: 0;
    background: transparent;
    border-radius: 0.25rem;
}

.file-manager-view-btn-active {
    color: var(--ui-text-subtle);
    cursor: default;
}

.file-manager-header-action-btn {
    cursor: pointer;
}

.file-manager-title-text {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 12rem;
}

.file-manager-list-table {
    min-width: 100%;
    table-layout: auto;
    border-bottom: 0;
}

.file-manager-col-title {
    width: 40%;
}

.file-manager-col-type {
    width: 12%;
}

.file-manager-col-size {
    width: 12%;
}

.file-manager-col-upload {
    width: 18%;
}

.file-manager-col-update {
    width: 12%;
}

.file-manager-col-actions {
    width: 6%;
}

.file-manager-list-sticky-th {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: var(--ui-surface-raised) !important;
    white-space: nowrap;
    box-shadow: none !important;
    border-bottom: 1px solid var(--border-ultralight) !important;
}

.file-manager-list-table tbody .table-listview-l-td {
    border-bottom: 1px solid var(--border-ultralight);
}

@media (min-width: 1400px) {
.file-manager-tile-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}
}

@media (min-width: 1800px) {
.file-manager-tile-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
}
}
</style>
